import { useEffect, useRef, useState } from 'react';
import { rovingTabIndex, useRovingRadioGroup } from '../components/rovingFocus';
import { ModalOverlay } from '../components/ModalOverlay';
import { useModalA11y } from '../components/useModalA11y';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  SwatchIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { SAVED_FILTER_NAME_MAX, type SavedFilter, type SavedFilterIcon } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from './format';
import { ApiError } from './api';
import { Portal } from '../components/Portal';
import { ConfirmModal } from '../taxonomy/ConfirmModal';
import { deleteSavedFilter, reorderSavedFilters, updateSavedFilter } from './saved-filters-api';
import {
  SAVED_FILTER_ICON_LABEL_KEY,
  SAVED_FILTER_ICON_ORDER,
  SavedFilterGlyph,
} from './saved-filter-icons';

/**
 * Écran « GÉRER MES FILTRES » : renommer (PATCH), changer d'icône (PATCH), supprimer
 * (DELETE + confirmation) et RÉORDONNER par boutons MONTER/DESCENDRE (PUT /order —
 * JAMAIS de glisser-déposer comme seule interaction, a11y). Vraie boîte de dialogue
 * (focus, `Échap`, clic sur le fond ; le caller l'enveloppe d'un `<Portal>`). La modale
 * porte une copie de travail de la liste, appelle l'API et remonte la liste à jour au
 * parent (`onChanged`) pour garder le menu « Mes filtres » synchronisé.
 */

interface ManageSavedFiltersModalProps {
  libraryId: string;
  filters: SavedFilter[];
  /** Remonte la liste à jour après chaque mutation (garde le menu synchronisé). */
  onChanged: (filters: SavedFilter[]) => void;
  onClose: () => void;
}

export function ManageSavedFiltersModal({
  libraryId,
  filters,
  onChanged,
  onClose,
}: ManageSavedFiltersModalProps): React.JSX.Element {
  const { t } = useI18n();
  const [list, setList] = useState<SavedFilter[]>(filters);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  // Renommage en cours (id + valeur d'édition) ; picker d'icône ouvert (id) ; cible de suppression.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [iconPickerId, setIconPickerId] = useState<string | null>(null);
  // ACTIVATION MANUELLE : `changeIcon` est un PATCH immédiat sur le filtre enregistré.
  // Faire suivre la sélection au focus enverrait une requête par flèche.
  const onIconKeyDown = useRovingRadioGroup('manual');
  const [deleteTarget, setDeleteTarget] = useState<SavedFilter | null>(null);

  const renameInputRef = useRef<HTMLInputElement>(null);
  // Focalise le champ de renommage à son apparition (sans `autoFocus`, non configuré ici).
  useEffect(() => {
    if (renamingId !== null) renameInputRef.current?.focus();
  }, [renamingId]);

  const commit = (next: SavedFilter[]): void => {
    setList(next);
    onChanged(next);
  };

  // 409 : deux causes DISTINCTES, discriminées par le code machine-lisible.
  //  · `STALE_UPDATE` (audit A6) = le filtre a été modifié ailleurs depuis notre lecture.
  //    Message ACTIONNABLE (« rechargez »), jamais un écrasement silencieux.
  //  · sinon = nom déjà pris (renommage). Tout le reste : repli fourni par l'appelant.
  const friendly = (err: unknown, fallback: string): string => {
    if (!(err instanceof ApiError) || err.status !== 409) return fallback;
    return err.code === 'STALE_UPDATE'
      ? t('savedFilters.errorStale')
      : t('savedFilters.errorNameTaken');
  };

  // Réordonne (échange avec le voisin) via PUT /order. Refuse aux bords.
  const move = async (index: number, delta: -1 | 1): Promise<void> => {
    const target = index + delta;
    if (busy || target < 0 || target >= list.length) return;
    const ids = list.map((f) => f.id);
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    setBusy(true);
    setError(undefined);
    try {
      const updated = await reorderSavedFilters(libraryId, ids);
      commit(updated);
    } catch (err) {
      setError(friendly(err, t('savedFilters.errorReorder')));
    } finally {
      setBusy(false);
    }
  };

  const startRename = (f: SavedFilter): void => {
    setIconPickerId(null);
    setError(undefined);
    setRenamingId(f.id);
    setRenameValue(f.name);
  };

  /**
   * Garde de RÉ-ENTRANCE (audit F5, cas jumeau de `TaxonomyPage`) — un `ref`, pas
   * l'état `busy` : `commitRename` est câblée sur Entrée (`onKeyDown`) ET sur `onBlur`
   * du même champ. La garde `busy` seule dépend du moment où React a re-rendu ; le ref
   * est posé synchronement, avant tout rendu, donc infranchissable.
   */
  const renamingRef = useRef(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y({ dialogRef, onEscape: !busy && deleteTarget === null ? onClose : undefined });

  const commitRename = async (f: SavedFilter): Promise<void> => {
    if (renamingRef.current) return; // renommage déjà en vol (Entrée puis blur)
    const name = renameValue.trim();
    if (busy) return;
    if (name === '' || name === f.name) {
      setRenamingId(null);
      return;
    }
    renamingRef.current = true;
    setBusy(true);
    setError(undefined);
    try {
      // Jeton de version (audit A6) : l'`updatedAt` du filtre TEL QUE nous l'avons lu.
      const updated = await updateSavedFilter(libraryId, f.id, {
        name,
        expectedUpdatedAt: f.updatedAt,
      });
      commit(list.map((x) => (x.id === f.id ? updated : x)));
      setRenamingId(null);
    } catch (err) {
      setError(friendly(err, t('savedFilters.errorUpdate')));
    } finally {
      renamingRef.current = false;
      setBusy(false);
    }
  };

  const changeIcon = async (f: SavedFilter, icon: SavedFilterIcon | null): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const updated = await updateSavedFilter(libraryId, f.id, {
        icon,
        expectedUpdatedAt: f.updatedAt,
      });
      commit(list.map((x) => (x.id === f.id ? updated : x)));
      setIconPickerId(null);
    } catch (err) {
      setError(friendly(err, t('savedFilters.errorUpdate')));
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (deleteTarget === null || busy) return;
    setBusy(true);
    setError(undefined);
    try {
      await deleteSavedFilter(libraryId, deleteTarget.id);
      commit(list.filter((x) => x.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setError(friendly(err, t('savedFilters.errorDelete')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalOverlay onDismiss={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('savedFilters.manageTitle')}
        className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="text-lg font-semibold text-fg">{t('savedFilters.manageTitle')}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            title={t('savedFilters.done')}
            aria-label={t('savedFilters.done')}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="flex min-h-0 flex-col gap-2 overflow-y-auto p-4 text-sm text-fg">
          {list.length === 0 ? (
            <p className="py-4 text-muted">{t('savedFilters.manageEmpty')}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {list.map((f, index) => (
                <li
                  key={f.id}
                  className="flex flex-col gap-2 rounded-md border border-border bg-surface-2 p-2"
                >
                  <div className="flex items-center gap-2">
                    <SavedFilterGlyph icon={f.icon} className="h-5 w-5 shrink-0 text-muted" />
                    {renamingId === f.id ? (
                      <input
                        type="text"
                        ref={renameInputRef}
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        maxLength={SAVED_FILTER_NAME_MAX}
                        aria-label={interpolate(t('savedFilters.renameLabel'), { name: f.name })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void commitRename(f);
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            setRenamingId(null);
                          }
                        }}
                        onBlur={() => void commitRename(f)}
                        className="h-8 min-w-0 flex-1 rounded-md border border-border bg-surface px-2 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                      />
                    ) : (
                      <span className="min-w-0 flex-1 truncate font-medium text-fg">{f.name}</span>
                    )}

                    {/* Réordonnancement par boutons (jamais de glisser-déposer seul). */}
                    <button
                      type="button"
                      onClick={() => void move(index, -1)}
                      disabled={busy || index === 0}
                      title={interpolate(t('savedFilters.moveUp'), { name: f.name })}
                      aria-label={interpolate(t('savedFilters.moveUp'), { name: f.name })}
                      className="rounded-md p-1.5 text-muted hover:bg-surface hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-40"
                    >
                      <ArrowUpIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => void move(index, 1)}
                      disabled={busy || index === list.length - 1}
                      title={interpolate(t('savedFilters.moveDown'), { name: f.name })}
                      aria-label={interpolate(t('savedFilters.moveDown'), { name: f.name })}
                      className="rounded-md p-1.5 text-muted hover:bg-surface hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-40"
                    >
                      <ArrowDownIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setRenamingId(null);
                        setIconPickerId((cur) => (cur === f.id ? null : f.id));
                      }}
                      disabled={busy}
                      title={interpolate(t('savedFilters.changeIcon'), { name: f.name })}
                      aria-label={interpolate(t('savedFilters.changeIcon'), { name: f.name })}
                      aria-expanded={iconPickerId === f.id}
                      className="rounded-md p-1.5 text-muted hover:bg-surface hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-40"
                    >
                      <SwatchIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => startRename(f)}
                      disabled={busy}
                      title={interpolate(t('savedFilters.renameLabel'), { name: f.name })}
                      aria-label={interpolate(t('savedFilters.renameLabel'), { name: f.name })}
                      className="rounded-md p-1.5 text-muted hover:bg-surface hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-40"
                    >
                      <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(f)}
                      disabled={busy}
                      title={t('savedFilters.delete')}
                      aria-label={interpolate(t('savedFilters.deleteAria'), { name: f.name })}
                      className="rounded-md p-1.5 text-danger hover:bg-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-40"
                    >
                      <TrashIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>

                  {/* Sélecteur d'icône inline (allow-list + « aucune »), coche + aria-checked. */}
                  {iconPickerId === f.id && (
                    <div
                      role="radiogroup"
                      aria-label={interpolate(t('savedFilters.changeIcon'), { name: f.name })}
                      onKeyDown={onIconKeyDown}
                      className="flex flex-wrap gap-1.5 border-t border-border pt-2"
                    >
                      <IconOption
                        selected={f.icon === null}
                        label={t('savedFilters.iconNone')}
                        onSelect={() => void changeIcon(f, null)}
                        disabled={busy}
                      >
                        <NoSymbolIcon className="h-5 w-5" aria-hidden="true" />
                      </IconOption>
                      {SAVED_FILTER_ICON_ORDER.map((name) => (
                        <IconOption
                          key={name}
                          selected={f.icon === name}
                          label={t(SAVED_FILTER_ICON_LABEL_KEY[name])}
                          onSelect={() => void changeIcon(f, name)}
                          disabled={busy}
                        >
                          <SavedFilterGlyph icon={name} className="h-5 w-5" />
                        </IconOption>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {error && (
            <p role="alert" className="flex items-start gap-2 text-danger">
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span>{error}</span>
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            {t('savedFilters.done')}
          </button>
        </div>
      </div>

      {/* Confirmation de suppression (définitive côté DB). */}
      {deleteTarget !== null && (
        <Portal>
          <ConfirmModal
            title={t('savedFilters.deleteConfirmTitle')}
            confirmLabel={t('savedFilters.deleteConfirm')}
            cancelLabel={t('savedFilters.cancel')}
            busyLabel={t('savedFilters.deleting')}
            busy={busy}
            danger
            onConfirm={() => void confirmDelete()}
            onClose={() => setDeleteTarget(null)}
          >
            <p>{interpolate(t('savedFilters.deleteConfirmBody'), { name: deleteTarget.name })}</p>
          </ConfirmModal>
        </Portal>
      )}
    </ModalOverlay>
  );
}

/** Bouton de choix d'icône (radio) : coche + `aria-checked`, bordure `brand` quand actif. */
function IconOption({
  selected,
  label,
  onSelect,
  disabled,
  children,
}: {
  selected: boolean;
  label: string;
  onSelect: () => void;
  disabled: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      tabIndex={rovingTabIndex(selected)}
      onClick={onSelect}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={`relative flex h-9 w-9 items-center justify-center rounded-md border focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50 ${
        selected ? 'border-brand bg-brand/10 text-brand' : 'border-border text-fg hover:bg-surface'
      }`}
    >
      {children}
      {selected && (
        <CheckIcon
          className="absolute -end-1 -top-1 h-4 w-4 rounded-full bg-brand p-0.5 text-brand-fg"
          aria-hidden="true"
        />
      )}
    </button>
  );
}
